import { CamelCasedPropertiesDeep } from "type-fest";
import { ClientOutput } from "../client";
import { BackpackServiceError, isBackpackResponseErrorBody } from "../errors";

// replicate legacy /building and /parking routes that returned 404 if no building/parking existed
export class NotFoundError extends Error {
  response: { statusCode: number };
  constructor(message: string) {
    super(message);
    this.name = "NotFoundError";
    this.response = { statusCode: 404 };
  }
}

// BE validates string ID and OpenAPI spec types "properties" property incompatibly
// since we're just sending the GeoJSON as is and BE isn't modifying it, it's easier and safe to use the wider type
export type FootprintGeoJsonFeature = GeoJSON.Feature;

export type BuildingSnakeCase = Omit<
  ClientOutput["tenantsAndSpaces"]["Building"],
  "footprint_geojson"
> & { footprint_geojson?: FootprintGeoJsonFeature | null };
export type Building = CamelCasedPropertiesDeep<BuildingSnakeCase>;
export type BuildingData = Omit<Building, "id"> & { id: number };

// The spec marks `use_type_code` as required on `Parking`, but the client
// constructs parking objects for writes/tests without it (as it did while the
// field was optional), so keep it optional here to preserve that behavior.
// the BE writes this to "P" so it doesn't need to be passed in body
export type SnakeCaseDetachedParking = Omit<
  ClientOutput["tenantsAndSpaces"]["Parking"],
  "footprint_geojson" | "use_type_code"
> & {
  footprint_geojson?: FootprintGeoJsonFeature | null;
  use_type_code?: ClientOutput["tenantsAndSpaces"]["StructureUseTypeCode"];
};
export type DetachedParking =
  CamelCasedPropertiesDeep<SnakeCaseDetachedParking>;
export type DetachedParkingData = Omit<DetachedParking, "id"> & { id: number };

type SnakeCaseSurfaceLot = ClientOutput["tenantsAndSpaces"]["SurfaceLot"];
export type SurfaceLot = CamelCasedPropertiesDeep<SnakeCaseSurfaceLot>;
export type SurfaceLotData = Omit<SurfaceLot, "id"> & { id: number };

export type ParkingSnakeCase = Omit<
  ClientOutput["tenantsAndSpaces"]["ParkingDetails"],
  "parking"
> & { parking: SnakeCaseDetachedParking[] };
type Parking = CamelCasedPropertiesDeep<ParkingSnakeCase>;
export type ParkingData = {
  parking: DetachedParkingData[];
  surfaceLots: SurfaceLotData[];
};

export type LegacyParking = {
  parking?: DetachedParking;
  surfaceLotId?: Parking["surfaceLots"][number]["id"];
};
export type LegacyParkingBody = {
  parking?: DetachedParking;
  hasSurfaceLot: boolean;
};
export type Floor = CamelCasedPropertiesDeep<
  ClientOutput["tenantsAndSpaces"]["Floor"]
>;

type ApiSpecSpace =
  ClientOutput["tenantsAndSpaces"]["SpaceWithEnergyStarAttributesWithRequiredFields"];
type SnakeCaseSpace = Omit<ApiSpecSpace, "tenant_name"> & {
  tenant: ApiSpecSpace["tenant_name"];
};
export type Space = CamelCasedPropertiesDeep<SnakeCaseSpace>;

export type FloorWithSpaces = CamelCasedPropertiesDeep<
  Omit<ClientOutput["tenantsAndSpaces"]["FloorsWithSpaces"], "spaces">
> & { spaces: Space[] };

/**
 * uuid/v4 is used for generating unique ids when adding new floors
 * so we need to widen the id type to allow strings for use in the form and office site setup viz
 */
export type ClientSideFloor = Omit<Floor, "id"> & {
  id?: string | number | null;
};

export function throwServiceError(error: any) {
  if (isBackpackResponseErrorBody(error)) {
    throw new BackpackServiceError(error);
  }
  throw new Error(error);
}
